/**
 * Client-side image preparation before upload (images-as-data, Phase 2).
 *
 * Re-encodes the picked file through a canvas, which strips EXIF, honours the
 * capture orientation, and caps the dimension:
 *   - compress=true  (routine): downscale to ~2048px + JPEG q0.8 — small bytes.
 *   - compress=false (NDT):     keep near-original quality (q0.95) but still cap a
 *     huge dimension + drop EXIF, so a 12 MP phone photo shrinks without visible loss.
 * On a browser that can't decode the type (e.g. HEIC on some engines) it falls back
 * to the original bytes untouched — the backend caps size and the mobile app is the
 * primary compressor there.
 */

export interface PreparedImage {
  blob: Blob;
  width: number;
  height: number;
  /** Whether the routine (lossy) path ran — recorded on the image datapoint. */
  compressed: boolean;
}

export interface PrepareOpts {
  compress: boolean;
  /** Max long-edge px; defaults to 2048 (routine) / 4000 (NDT). */
  maxDimension?: number;
}

export async function prepareImageForUpload(
  file: File,
  opts: PrepareOpts,
): Promise<PreparedImage> {
  const maxDim =
    opts.maxDimension && opts.maxDimension > 0
      ? opts.maxDimension
      : opts.compress
        ? 2048
        : 4000;
  const quality = opts.compress ? 0.8 : 0.95;

  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
  } catch {
    // Undecodable in this browser — send the original; backend caps size.
    return { blob: file, width: 0, height: 0, compressed: false };
  }

  const { width: ow, height: oh } = bitmap;
  const scale = Math.min(1, maxDim / Math.max(ow, oh));
  const w = Math.max(1, Math.round(ow * scale));
  const h = Math.max(1, Math.round(oh * scale));

  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    bitmap.close();
    return { blob: file, width: ow, height: oh, compressed: false };
  }
  ctx.drawImage(bitmap, 0, 0, w, h);
  bitmap.close();

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob((b) => resolve(b), "image/jpeg", quality),
  );
  if (!blob) {
    return { blob: file, width: ow, height: oh, compressed: false };
  }
  // `compressed` reflects the tag's intent: routine tags mark true, NDT tags mark
  // false (quality preserved) even though we still re-encoded to shrink.
  return { blob, width: w, height: h, compressed: opts.compress };
}
